/**
 * حالات الشاشة الست — مكوّنات قابلة لإعادة الاستخدام (design/States.dc.html).
 *
 * القاعدة المعتمدة في الشاشة نفسها:
 *   «كل حالة بتقول السبب في سطر، وبتدّي خطوة واحدة بس يعملها الموظف».
 *
 * ⚠️ نصوص الأخطاء الجاية من السيستم بتتعرض زي ما هي (`message_ar`).
 *    الواجهة ما بتصيغش رسالة خطأ من عندها — بس بتحطها في المكان ده.
 */
import type { ReactNode } from "react";
import { Icon, type IconName } from "@/components/Icon";
import { Num } from "@/lib/format";

type Tone = "neutral" | "danger";

export function ScreenState({
  icon,
  tone = "neutral",
  title,
  hint,
  action,
  extra,
  fill = false,
  className = "card",
}: {
  icon: IconName;
  tone?: Tone;
  title: ReactNode;
  hint?: ReactNode;
  action?: { label: string; onClick: () => void };
  extra?: ReactNode;
  fill?: boolean;
  className?: string;
}) {
  return (
    <div className={`${className} state${fill ? " fill" : ""}`} role="status">
      <span className={`state-mark${tone === "danger" ? " danger" : ""}`}>
        <Icon name={icon} size={17} />
      </span>
      <div className="state-title clip">{title}</div>
      {hint ? <div className="state-hint">{hint}</div> : null}
      {action ? (
        <button type="button" className="btn sm ghost" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
      {extra}
    </div>
  );
}

/** (1) فاضي — خلّص شغله */
export function EmptyState({
  title = "مفيش مهام دلوقتي",
  hint = "خلّصت كل حاجة النهارده",
  action,
  fill,
}: {
  title?: string;
  hint?: string;
  action?: { label: string; onClick: () => void };
  fill?: boolean;
}) {
  return (
    <ScreenState icon="inbox" title={title} hint={hint} {...(action ? { action } : {})} {...(fill ? { fill } : {})} />
  );
}

/** (2) بيحمّل — هيكل عظمي بعدد صفوف يقارب المحتوى المتوقّع */
export function LoadingState({ rows = 3, fill }: { rows?: number; fill?: boolean }) {
  const widths = ["60%", "88%", "44%", "72%", "52%", "80%"];
  return (
    <div className={`card state loading${fill ? " fill" : ""}`} role="status" aria-live="polite">
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="skel" style={{ width: widths[i % widths.length] }} />
      ))}
      <div className="t-meta" style={{ marginTop: 4 }}>
        بيحمّل…
      </div>
    </div>
  );
}

/** (3) خطأ — بيعرض سبب السيستم زي ما هو */
export function ErrorState({
  messageAr = "مقدرناش نجيب البيانات",
  hint = "محاولة تانية أو التواصل مع الدعم",
  onRetry,
  fill,
}: {
  messageAr?: string;
  hint?: string;
  onRetry?: () => void;
  fill?: boolean;
}) {
  return (
    <ScreenState
      icon="circle-alert"
      tone="danger"
      title={messageAr}
      hint={hint}
      {...(onRetry ? { action: { label: "إعادة المحاولة", onClick: onRetry } } : {})}
      {...(fill ? { fill } : {})}
    />
  );
}

/** (4) بحث بلا نتيجة */
export function NoResultsState({
  query,
  onClear,
  fill,
}: {
  query: string;
  onClear?: () => void;
  fill?: boolean;
}) {
  return (
    <ScreenState
      icon="search"
      title={`مفيش نتيجة لـ«${query}»`}
      hint="البحث باسم تاني أو بالرقم"
      {...(onClear ? { action: { label: "مسح البحث", onClick: onClear } } : {})}
      {...(fill ? { fill } : {})}
    />
  );
}

/** (5) بلا صلاحية — نفس نص شاشة الحالات */
export function NoPermissionState({
  messageAr = "مش من صلاحياتك",
  onBack,
  fill,
}: {
  messageAr?: string;
  onBack?: () => void;
  fill?: boolean;
}) {
  return (
    <ScreenState
      icon="lock"
      title={messageAr}
      hint="التواصل مع إدارة الأكاديمية"
      {...(onBack ? { action: { label: "رجوع لمكتبي", onClick: onBack } } : {})}
      {...(fill ? { fill } : {})}
    />
  );
}

/** (6) بلا اتصال — مختلفة عن «خطأ»: السيستم شغّال بس الجهاز مش واصل */
export function OfflineState({ onRetry, fill }: { onRetry?: () => void; fill?: boolean }) {
  return (
    <ScreenState
      icon="wifi-off"
      title="مفيش اتصال بالسيستم دلوقتي"
      hint="راجع النت وجرّب تاني — شغلك محفوظ"
      {...(onRetry ? { action: { label: "إعادة المحاولة", onClick: onRetry } } : {})}
      {...(fill ? { fill } : {})}
    />
  );
}

/** (إضافية من شاشة الحالات) أول استخدام — لسه مفيش داتا أصلًا */
export function FirstUseState({
  title = "لسه مفيش طلبات",
  hint = "هيظهر أول ما حد يملأ الفورم",
  connected = true,
  fill,
}: {
  title?: string;
  hint?: string;
  connected?: boolean;
  fill?: boolean;
}) {
  return (
    <ScreenState
      icon="file"
      title={title}
      hint={hint}
      {...(fill ? { fill } : {})}
      extra={
        <span className={`chip ${connected ? "okc" : "ghost"}`}>
          <span className="bullet" />
          {connected ? "الفورم متصل" : "الفورم مش متصل"}
        </span>
      }
    />
  );
}

/** عدّاد صغير بيستخدم في نصوص الحالات — أرقام لاتينية إجباري */
export function StateCount({ n }: { n: number }) {
  return <Num v={n} />;
}
